import { CheckCircle2, Pencil, Trash2, X } from 'lucide-react';
import { MoneyAmount } from '@/components/design-system';
import { StatutPaiementBadge, isPaiementEnRetard } from '@/components/finances/StatutPaiementBadge';
import type { DetteFournisseurRow } from '@/hooks/queries/useDettesFournisseurs';
import { cn } from '@/lib/utils';

interface Props {
  dette: DetteFournisseurRow | null;
  onClose: () => void;
  onEnregistrerPaiement: (d: DetteFournisseurRow) => void;
  onEdit: (d: DetteFournisseurRow) => void;
  onDelete: (d: DetteFournisseurRow) => void;
}

export function DetteFournisseurDrawer({
  dette,
  onClose,
  onEnregistrerPaiement,
  onEdit,
  onDelete,
}: Props): JSX.Element {
  if (!dette) return <></>;
  const reste = dette.montant_dette - dette.montant_regle;
  const enRetard = isPaiementEnRetard(dette.statut, dette.date_formation);

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-slate-900/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col border-l border-slate-200 bg-white shadow-2xl sm:max-w-lg"
        role="dialog"
        aria-label="Détail dette fournisseur"
      >
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 p-4">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
              Dette fournisseur
            </div>
            <h2 className="mt-1 truncate text-lg font-bold text-slate-900">
              {dette.fournisseur_nom}
            </h2>
            <div className="mt-1 flex items-center gap-2">
              <StatutPaiementBadge statut={dette.statut} enRetard={enRetard} />
              <span className="text-xs text-slate-500">
                {dette.annee}
                {dette.reference_facture ? ` · ${dette.reference_facture}` : ''}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => onEdit(dette)}
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Modifier"
              title="Modifier"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => onDelete(dette)}
              className="rounded-md p-1.5 text-rose-500 hover:bg-rose-50 hover:text-rose-700"
              aria-label="Supprimer"
              title="Supprimer"
            >
              <Trash2 className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </header>
        <div className="flex-1 space-y-5 overflow-y-auto p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl bg-gradient-to-br from-orange-50 to-amber-100 p-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-orange-700">
                Montant dette
              </div>
              <div className="mt-1">
                <MoneyAmount amount={dette.montant_dette} variant="kpi" />
              </div>
            </div>
            <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-teal-100 p-4">
              <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">
                Reste à payer
              </div>
              <div className="mt-1">
                <MoneyAmount amount={reste} variant="kpi" withColor />
              </div>
              {dette.montant_regle > 0 && (
                <div className="mt-1 text-[11px] text-slate-600">
                  Déjà réglé : {dette.montant_regle.toLocaleString('fr-DZ')} DA
                </div>
              )}
            </div>
          </div>

          <Section title="Coordonnées fournisseur">
            <Field label="Téléphone" value={dette.fournisseur_telephone ?? '—'} />
            <Field label="Email" value={dette.fournisseur_email ?? '—'} />
            <Field label="RIB" value={dette.fournisseur_rib ?? '—'} mono />
          </Section>

          <Section title="Facture">
            <Field label="Référence" value={dette.reference_facture ?? '—'} mono />
            <Field label="Formation liée" value={dette.formation_titre ?? '—'} />
            <Field label="Date formation" value={dette.date_formation ?? '—'} />
          </Section>

          {dette.montant_regle > 0 && (
            <Section title="Dernier règlement">
              <Field label="Date virement" value={dette.date_virement ?? '—'} />
              <Field label="Mode" value={dette.mode_paiement ?? '—'} />
              <Field label="Référence" value={dette.reference_operation ?? '—'} mono />
              {dette.operation_id && (
                <div className="mt-1 text-[11px] text-slate-500">
                  Lié à l&apos;opération{' '}
                  <span className="font-mono">{dette.operation_id.slice(0, 8)}…</span>
                </div>
              )}
            </Section>
          )}

          {dette.observation && (
            <Section title="Observation">
              <p className="text-sm text-slate-700">{dette.observation}</p>
            </Section>
          )}
        </div>
        <footer className="border-t border-slate-200 p-4">
          {dette.statut !== 'paye' ? (
            <button
              type="button"
              onClick={() => onEnregistrerPaiement(dette)}
              className={cn(
                'inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-700',
              )}
            >
              <CheckCircle2 className="h-4 w-4" />
              {dette.statut === 'partiel' ? 'Enregistrer un règlement' : 'Enregistrer un paiement'}
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 w-full items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Fermer
            </button>
          )}
        </footer>
      </aside>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <section>
      <h3 className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">
        {title}
      </h3>
      <div className="space-y-1.5">{children}</div>
    </section>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className={cn('text-right font-medium text-slate-800', mono && 'font-mono text-xs')}>
        {value}
      </span>
    </div>
  );
}
